import React, { useEffect, useState } from "react";
import CustomModal from "../../component/Modal";
import CustomButton from "../../component/Button";
import type { PolicyBullet, PolicySection } from "../../util/companySafetyPolicyContent";
import { VscLoading } from "react-icons/vsc";

interface SectionEditModalProps {
  open: boolean;
  section: PolicySection | null;
  saving?: boolean;
  editableTitle?: boolean;
  onClose: () => void;
  onSave: (section: PolicySection) => void;
}

const cloneSection = (section: PolicySection): PolicySection => ({
  ...section,
  paragraphs: [...section.paragraphs],
  bullets: section.bullets?.map((b) => ({ ...b })),
});

const SectionEditModal: React.FC<SectionEditModalProps> = ({
  open,
  section,
  saving = false,
  editableTitle = false,
  onClose,
  onSave,
}) => {
  const [draft, setDraft] = useState<PolicySection | null>(null);

  useEffect(() => {
    if (section && open) {
      setDraft(cloneSection(section));
    }
  }, [section, open]);

  if (!draft) return null;

  const updateParagraph = (index: number, value: string) => {
    setDraft((prev) => {
      if (!prev) return prev;
      const paragraphs = [...prev.paragraphs];
      paragraphs[index] = value;
      return { ...prev, paragraphs };
    });
  };

  const addParagraph = () => {
    setDraft((prev) =>
      prev ? { ...prev, paragraphs: [...prev.paragraphs, ""] } : prev
    );
  };

  const removeParagraph = (index: number) => {
    setDraft((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        paragraphs: prev.paragraphs.filter((_, i) => i !== index),
      };
    });
  };

  const updateBullet = (index: number, field: keyof PolicyBullet, value: string) => {
    setDraft((prev) => {
      if (!prev?.bullets) return prev;
      const bullets = prev.bullets.map((b, i) =>
        i === index ? { ...b, [field]: value } : b
      );
      return { ...prev, bullets };
    });
  };

  const addBullet = () => {
    setDraft((prev) => ({
      ...prev!,
      bullets: [...(prev?.bullets ?? []), { text: "" }],
    }));
  };

  const removeBullet = (index: number) => {
    setDraft((prev) => {
      if (!prev?.bullets) return prev;
      return { ...prev, bullets: prev.bullets.filter((_, i) => i !== index) };
    });
  };

  const handleSave = () => {
    if (!draft) return;
    const cleaned: PolicySection = {
      ...draft,
      paragraphs: draft.paragraphs.map((p) => p.trim()).filter(Boolean),
      bullets: draft.bullets
        ?.map((b) => ({
          label: b.label?.trim() || undefined,
          text: b.text.trim(),
        }))
        .filter((b) => b.text),
    };
    onSave(cleaned);
  };

  return (
    <CustomModal
      open={open}
      onClose={onClose}
      label={`Edit — ${draft.title}`}
      maxWidth={720}
      contentClassName="company-policy-edit-modal"
    >
      <div className="company-policy-edit-form">
        {editableTitle ? (
          <div className="company-policy-edit-form__block">
            <p className="company-policy-edit-form__label">Section title</p>
            <input
              className="company-policy-edit-form__input"
              value={draft.title}
              onChange={(e) =>
                setDraft((prev) => (prev ? { ...prev, title: e.target.value } : prev))
              }
              placeholder="Section title"
            />
          </div>
        ) : null}

        <div className="company-policy-edit-form__block">
          <p className="company-policy-edit-form__label">Paragraphs</p>
          {draft.paragraphs.length === 0 ? (
            <p className="company-policy-edit-form__empty">No paragraphs yet.</p>
          ) : null}
          {draft.paragraphs.map((paragraph, idx) => (
            <div key={idx} className="company-policy-edit-form__row">
              <textarea
                className="company-policy-edit-form__textarea"
                value={paragraph}
                rows={4}
                onChange={(e) => updateParagraph(idx, e.target.value)}
                placeholder={`Paragraph ${idx + 1}`}
              />
              <button
                type="button"
                className="company-policy-edit-form__remove"
                onClick={() => removeParagraph(idx)}
                aria-label="Remove paragraph"
              >
                Remove
              </button>
            </div>
          ))}
          <button
            type="button"
            className="company-policy-edit-form__add"
            onClick={addParagraph}
          >
            + Add paragraph
          </button>
        </div>

        <div className="company-policy-edit-form__block">
          <p className="company-policy-edit-form__label">Bullet points</p>
          {(draft.bullets ?? []).length === 0 ? (
            <p className="company-policy-edit-form__empty">No bullet points.</p>
          ) : null}
          {(draft.bullets ?? []).map((bullet, idx) => (
            <div key={idx} className="company-policy-edit-form__bullet">
              <input
                className="company-policy-edit-form__input"
                value={bullet.label ?? ""}
                onChange={(e) => updateBullet(idx, "label", e.target.value)}
                placeholder="Label (optional)"
              />
              <textarea
                className="company-policy-edit-form__textarea"
                value={bullet.text}
                rows={3}
                onChange={(e) => updateBullet(idx, "text", e.target.value)}
                placeholder="Bullet text"
              />
              <button
                type="button"
                className="company-policy-edit-form__remove"
                onClick={() => removeBullet(idx)}
              >
                Remove bullet
              </button>
            </div>
          ))}
          <button
            type="button"
            className="company-policy-edit-form__add"
            onClick={addBullet}
          >
            + Add bullet
          </button>
        </div>

        <div className="company-policy-edit-form__actions">
          <CustomButton
            title="Cancel"
            onClick={onClose}
            variant="outlined"
            bgColor="bg-transparent"
            textColor="text-primaryText"
            className="!border !border-white/20 !px-5"
            disabled={saving}
          />
          <CustomButton
            title={
              saving ? (
                <VscLoading className="animate-spin text-xl" />
              ) : (
                "Save section"
              )
            }
            onClick={handleSave}
            disabled={saving}
            className="!px-5"
          />
        </div>
      </div>
    </CustomModal>
  );
};

export default SectionEditModal;
